import { useCallback, useEffect, useRef, useState } from 'react';

import { alignmentScore, eyeCentres } from '@/lib/align';
import type { EyePair } from '@/lib/encode';
import type {
  LandmarkerRequest,
  LandmarkerResponse,
} from '@/workers/faceLandmarker.worker';

export type AlignmentStatus = 'loading' | 'ready' | 'unavailable';

export interface FaceReading {
  /** Eye centres normalised against the *source* frame. */
  eyes: EyePair;
  score: number;
}

interface UseFaceAlignmentResult {
  status: AlignmentStatus;
  /** Latest reading, or null while no face is in frame. */
  reading: FaceReading | null;
  /** Detect against one video frame. Safe to call before the model is ready. */
  track: (video: HTMLVideoElement) => void;
}

/**
 * Tracks the face in the preview.
 *
 * Only eye positions come back, not a finished matrix: the transform depends on
 * how the source frame is fitted into the output, which is known at encode time
 * and not here.
 *
 * Everything degrades to null rather than throwing. The model is 3.6 MB fetched
 * by `make models`, and a missing one has to leave the user able to take their
 * photo — unaligned — rather than blocking the shutter.
 */
export function useFaceAlignment(enabled: boolean): UseFaceAlignmentResult {
  const workerRef = useRef<Worker | null>(null);
  const busyRef = useRef(false);
  const [status, setStatus] = useState<AlignmentStatus>('loading');
  const [reading, setReading] = useState<FaceReading | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const worker = new Worker(
      new URL('../workers/faceLandmarker.worker.ts', import.meta.url),
      { type: 'module' },
    );

    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<LandmarkerResponse>) => {
      const message = event.data;

      if (message.type === 'ready') {
        setStatus('ready');

        return;
      }

      if (message.type === 'unavailable') {
        setStatus('unavailable');

        return;
      }

      busyRef.current = false;

      const eyes = message.landmarks ? eyeCentres(message.landmarks) : null;

      setReading(eyes ? { eyes, score: alignmentScore(eyes.left, eyes.right) } : null);
    };

    worker.postMessage({ type: 'init' } satisfies LandmarkerRequest);

    return () => {
      worker.terminate();
      workerRef.current = null;
      busyRef.current = false;
      setStatus('loading');
      setReading(null);
    };
  }, [enabled]);

  const track = useCallback((video: HTMLVideoElement) => {
    // One frame in flight at a time. Queueing every animation frame behind a
    // slower detector builds a backlog that only ever grows.
    if (!workerRef.current || busyRef.current || !video.videoWidth) {
      return;
    }

    busyRef.current = true;

    void createImageBitmap(video).then(
      (bitmap) =>
        workerRef.current?.postMessage(
          {
            type: 'detect',
            bitmap,
            timestamp: performance.now(),
          } satisfies LandmarkerRequest,
          [bitmap],
        ),
      () => {
        busyRef.current = false;
      },
    );
  }, []);

  return { status, reading, track };
}
